import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import Input from '../../../components/Input';
import { t } from 'i18next';
import NumbersInput from '../../../components/NumbersInput';
import { ICuser, INumbers } from 'customTypes';
import IssueGuide from '../../../components/IssueGuide';
import SmallTextLabel from '../../../components/button/SmallTextLabel';
import { IoPerson } from 'react-icons/io5';
import { useRecoilValue } from 'recoil';
import { IUser, userState } from '../../../atoms';

interface IForm {
  rowState: ICuser | null;
  setRowState: Dispatch<SetStateAction<ICuser | null>>;
}

const telLengthState = { num1: 3, num2: 4, num3: 4 }; // 전화번호 입력 길이

const Form = ({ rowState, setRowState }: IForm) => {
  const userInfo = useRecoilValue<IUser>(userState);
  const [telNumState, setTelNumState] = useState<INumbers>({ num1: '', num2: '', num3: '' });
  const [hpNumState, setHpNumState] = useState<INumbers>({ num1: '010', num2: '', num3: '' });
  const [emerNumState, setEmerNumState] = useState<INumbers>({ num1: '', num2: '', num3: '' });

  useEffect(() => {
    if (rowState) {
      const splitTelNumber = rowState.telNum?.split('-') ?? ['', '', ''];
      setTelNumState({ num1: splitTelNumber[0], num2: splitTelNumber[1], num3: splitTelNumber[2] });

      const splitHpNumber = rowState.hpNum?.split('-') ?? ['010', '', ''];
      setHpNumState({ num1: splitHpNumber[0], num2: splitHpNumber[1], num3: splitHpNumber[2] });

      const splitEmerNumber = rowState.emerNum?.split('-') ?? ['', '', ''];
      setEmerNumState({ num1: splitEmerNumber[0], num2: splitEmerNumber[1], num3: splitEmerNumber[2] });
    }
  }, [rowState]);

  if (!rowState) return <IssueGuide type='select' text='사원을 선택하세요' />;
  return (
    <Root>
      <div className='inputForm'>
        <div className='inputForm-group-1280'>
          <div className='image-wrap col-span-img'>
            <div className='logo-input-container'>{rowState.uImg ? <img src={rowState.uImg} alt='user_image' /> : <IoPerson size={80} />}</div>
          </div>
          <div className='inputForm-row labelInInput'>
            <Input label={t('사원명')} type='text' id='userName' name='userName' state={rowState} setState={setRowState} disabled />
          </div>
          <div className='inputForm-row labelInInput'>
            <Input label={t('이메일')} type='text' id='eMail' name='eMail' state={rowState} setState={setRowState} disabled />
          </div>
          <div className='inputForm-row labelInInput'>
            <Input label={t('근로 상태')} type='text' id='uWorkstatusName' name='uWorkstatusName' state={rowState} setState={setRowState} disabled />
          </div>
          <div className='inputForm-row labelInInput'>
            <Input label={t('부서명')} type='text' id='partName' name='partName' state={rowState} setState={setRowState} disabled />
          </div>
          <div className='inputForm-row labelInInput'>
            <Input label={t('직급')} type='text' id='positionName' name='positionName' state={rowState} setState={setRowState} disabled />
          </div>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='hpNum'>{t('휴대폰번호')}</label>
            <NumbersInput state={hpNumState} setState={setHpNumState} lengthState={telLengthState} disabled />
          </div>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='telNum'>{t('전화번호')}</label>
            <NumbersInput state={telNumState} setState={setTelNumState} lengthState={telLengthState} disabled />
          </div>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='emerNum'>{t('비상 연락처')}</label>
            <NumbersInput state={emerNumState} setState={setEmerNumState} lengthState={telLengthState} disabled />
          </div>
          <div className='inputForm-row labelInInput flex-center'>
            <Input label={t('생년월일')} type='text' id='uBdate' name='uBdate' state={rowState} setState={setRowState} disabled />
            <SmallTextLabel text={rowState.uBgubun === '0' ? '양력' : '음력'} color='orange' />
          </div>
          <div className='inputForm-row labelInInput'>
            <Input label={t('입사일자')} type='text' id='uJobdate' name='uJobdate' state={rowState} setState={setRowState} disabled />
          </div>
          <div className='inputForm-row labelInInput'>
            <Input label={t('퇴사일자')} type='text' id='uRetiredate' name='uRetiredate' state={rowState} setState={setRowState} disabled />
          </div>

          {(rowState.userId === userInfo.userId || userInfo.gCd === 'MM') && (
            <>
              <div className='inputForm-row labelInInput'>
                <Input label={t('연차일수')} type='text' id='uAnnualnum' name='uAnnualnum' state={rowState} setState={setRowState} disabled />
              </div>
              <div className='inputForm-row labelInInput'>
                <Input label={t('사용 연차일수')} type='text' id='uRannualnum' name='uRannualnum' state={rowState} setState={setRowState} disabled />
              </div>
            </>
          )}
        </div>
      </div>
    </Root>
  );
};

export default Form;

const Root = styled.div`
  padding: 0.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100% - 57px);
  overflow: auto;
  justify-content: flex-start;
  ul > li {
    width: 100% !important;
    max-width: none !important;
  }

  .inputForm {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.5rem;
    flex-grow: 1;
    overflow: auto;
    button {
      height: 2.5rem;
      font-size: 0.75rem;
      flex-shrink: 0;
      padding: 0 0.75rem;
      border-radius: 0.25rem;
    }
    button.gray {
      font-weight: 500;
      color: var(--zinc-200);
    }
    label {
      width: 4.5rem;
      font-weight: 500;
      flex-shrink: 0;
      font-size: 0.875rem;
      white-space: break-spaces;
      line-height: 1.4;
    }
    .inputForm-group-1280 {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      @media (min-width: 1280px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem 2rem;
      }
    }
    .inputForm-group-1536 {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      @media (min-width: 1536px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem 2rem;
      }
    }
    .inputForm-group-1536.withBtn {
      flex-direction: row;
      gap: 0.5rem;
      button {
        transition: none;
      }
      @media (min-width: 1536px) {
        gap: 1rem 2rem;
        button {
          margin-left: 0;
          width: fit-content;
        }
      }
    }
    .inputForm-group-1536.withBtn.inputColumnFour {
      input {
        padding: 0;
      }
    }
    .inputForm-row.labelInInput {
      display: flex;
      gap: 0.5rem;
      flex-grow: 1;
      > div {
        flex-grow: 1;
      }
    }
    .inputForm-row.labelOutInput,
    .inputForm-row.labelInInput > div > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
      input {
        flex-shrink: 1;
        height: 2.5rem;
        font-size: 0.875rem;
        /* padding: 0; */
        padding: 0 0.5rem;
        &:disabled {
          background-color: var(--tonal);
          color: var(--text-disabled);
        }
      }
      > .viewOnly {
        height: 2.5rem;
        font-size: 0.875rem;
        background-color: rgba(0, 0, 0, 0.05);
        color: var(--text-primary);
        border-radius: 0.25rem;
        padding: 0 0.75rem;
        display: flex;
        align-items: center;
      }
      > div {
        flex-grow: 1;
        > div > div > ul {
          height: 2.5rem;
          li {
            display: flex;
            span {
              flex-grow: 1;
              width: 4rem;
            }
          }
        }
      }
    }
    .detailInfo-group {
      margin: 1rem 0;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
      @media (min-width: 1536px) {
        grid-template-columns: repeat(3, 1fr);
      }
      .flex-col.detailInfo {
        flex-grow: 1;
        width: 100%;
        div {
          height: 2.5rem;
          color: var(--text-tertiary);
          background-color: var(--tonal);
          border-radius: 0.25rem;
          display: flex;
          align-items: center;
          padding: 0 0.5rem;
          font-size: 0.875rem;
        }
      }
    }
  }
  input {
    :disabled {
      background-color: #eeeeee;
    }
  }

  .col-span-img {
    grid-row: span 5;
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: 17.5rem;
    border-radius: 0.25rem;
    gap: 0.5rem;
    overflow: hidden;
    user-select: none;

    .logo-input-container {
      img {
        max-width: 20rem;
        max-height: 15rem;
        height: auto;
      }
      svg {
        color: var(--text-secondary);
      }
      flex-grow: 1;
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.5rem;
      background-color: var(--tonal);
      justify-content: space-around;
      align-items: center;
    }
    > div {
      display: flex;
      gap: 0.25rem;
    }
  }
  .userImageWrapper {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-height: 12rem;
    max-width: 18rem;
    justify-content: flex-end;
    overflow: hidden;
    color: var(--text-quaternary);
    label {
      width: fit-content;
      margin: 1rem 0 0 0;
      > div {
        line-height: 2;
        padding: 0 1rem;
        border-radius: 5px;
      }
    }

    img {
      max-height: 9rem;
    }

    .smallBtn {
      cursor: pointer;
      color: #eaeaea;
      background-color: #8950fc;
      border: 1px solid #8950fc;
      &:hover {
        color: #eaeaea;
        background-color: rgba(137, 80, 252, 0.6);
      }
    }
  }
  @media screen and (max-width: 1024px) {
  }
  @media screen and (max-width: 767px) {
    width: 100%;
    height: fit-content;
    overflow: auto;
  }
`;
